import React, { useEffect, useState } from "react";
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  TextField,
  Box,
  Typography,
  InputAdornment,
} from "@mui/material";
import {
  useUpdateCommissionIdMutation,
  useLazyGetCommissionQuery,
} from "../../services/commission";
import { showToast } from "../../constants/toasts";

interface CommissionModalProps {
  open: boolean;
  setOpen: (open: boolean) => void;
}

interface ServiceFees {
  serviceFees: number;
  _id: string;
  createdAt: string;
  updatedAt: string;
  __v: number;
}

const CommissionModal: React.FC<CommissionModalProps> = ({ open, setOpen }) => {
  const [commission, setCommission] = useState<string>("");
  const [isLoading, setIsLoading] = useState(false);
  const [getCommission] = useLazyGetCommissionQuery();
  const [updateData] = useUpdateCommissionIdMutation();
  const [data, setData] = useState<ServiceFees | null>(null);
  const [error, setError] = useState<string>("");

  const handleClose = () => {
    setOpen(false);
    setCommission("");
    setError("");
  };

  const handleCommissionChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const value = e.target.value.replace(/[^0-9.]/g, '');
    
    // Check if value is empty or matches pattern for numbers with max 2 decimal places
    if (value === "" || /^\d{0,2}(\.\d{0,2})?$/.test(value)) {
      setCommission(value);
      setError("");
    } else {
      setError("Please enter a valid number with up to 2 decimal places");
    }
  };

  const handleSubmit = async () => {
    if (!data?._id) return;
    if (!commission) {
      setError("Commission is required");
      return;
    }

    const commissionValue = Number(commission.replace('%', ''));
    if (commissionValue === 0) {
      setError("Commission cannot be 0%");
      return;
    }

    try {
      setIsLoading(true);
      const response = await updateData({
        id: data._id,
        body: { serviceFees: Number(commission.replace('%', '')) },
      }).unwrap();

      if (response?.statusCode === 200) {
        showToast("Commission set successfully");
        handleClose();
      }
    } catch (error) {
      console.error("Error updating commission:", error);
    } finally {
      setIsLoading(false);
    }
  };

  const getAllCommission = async () => {
    setIsLoading(true);
    try {
      const response = await getCommission().unwrap();
      if (response?.statusCode === 200) {
        setData(response?.data);
        setCommission(response?.data?.serviceFees?.toString() + '%' || '');
      }
    } catch (error) {
      console.error("Error fetching commission:", error);
    } finally {
      setIsLoading(false);
    }
  };

  useEffect(() => {
    if (open) {
      getAllCommission();
    }
  }, [open]);

  return (
    <Dialog open={open} onClose={handleClose} maxWidth="sm" fullWidth>
      <DialogTitle>
        <Typography variant="h6" component="div">
          Set Commission
        </Typography>
      </DialogTitle>
      <DialogContent>
        <Box sx={{ mt: 2 }}>
          <TextField
            fullWidth
            placeholder="Commission Amount"
            type="text"
            value={commission}
            onChange={handleCommissionChange}
            helperText={error || "Enter the commission amount"}
            error={!!error}
            disabled={isLoading}
          />
        </Box>
      </DialogContent>
      <DialogActions>
        <Button
          onClick={handleClose}
          sx={{
            color: "white",
            backgroundColor: "#BF2F17",
            "&:hover": {
              backgroundColor: "#BF2F17",
            },
            textTransform: "capitalize",
          }}
          disabled={isLoading}
        >
          Cancel
        </Button>
        <Button
          onClick={handleSubmit}
          variant="contained"
          sx={{
            color: "white",
            backgroundColor: "#BF2F17",
            "&:hover": {
              backgroundColor: "#BF2F17",
            },
            textTransform: "capitalize",
          }}
          disabled={isLoading}
        >
          {isLoading ? "Saving..." : "Save"}
        </Button>
      </DialogActions>
    </Dialog>
  );
};

export default CommissionModal;
